export type ValidationRule = {
  key: string;
  test: (value: string) => boolean;
};

export const requiredRule: ValidationRule = {
  key: 'required',
  test: value => value.trim().length > 0,
};

export const emailFormatRule: ValidationRule = {
  key: 'format',
  test: value => value.includes('@'),
};

export const emailRules: ValidationRule[] = [requiredRule, emailFormatRule];

export const cprFormatRule: ValidationRule = {
  key: 'cpr',
  test: value => value.trim() === '' || /^\d{6}-?\d{4}$/.test(value.trim()),
};

export const passwordRules: ValidationRule[] = [
  { key: 'length', test: value => value.length >= 8 },
  { key: 'case', test: value => /[a-z]/.test(value) && /[A-Z]/.test(value) },
  { key: 'number', test: value => /[0-9]/.test(value) },
  { key: 'specialchar', test: value => /[^A-Za-z0-9]/.test(value) },
];

export function setupFieldValidation(input: HTMLInputElement, rules: ValidationRule[]) {
  const container = input.closest<HTMLElement>('.form-group');
  if (!container) {
    return;
  }

  input.addEventListener('blur', () => {
    const firstFailed = rules.find(rule => !rule.test(input.value));

    for (const rule of rules) {
      container.querySelector<HTMLElement>(`[data-validation-message="${rule.key}"]`)
        ?.classList.toggle('hidden', rule.key !== firstFailed?.key);
    }
  });
}

export function setupPasswordToggle(input: HTMLInputElement) {
  const toggle = input.closest<HTMLElement>('.input-holder')?.querySelector<HTMLButtonElement>('.password-toggle');
  if (!toggle) {
    return;
  }

  const icon = toggle.querySelector<HTMLElement>('.password-toggle-icon');
  const showLabel = toggle.querySelector<HTMLElement>('[data-label-show]');
  const hideLabel = toggle.querySelector<HTMLElement>('[data-label-hide]');

  toggle.addEventListener('click', () => {
    const revealed = input.type === 'text';

    input.type = revealed ? 'password' : 'text';
    toggle.setAttribute('aria-pressed', String(!revealed));
    showLabel?.classList.toggle('hidden', !revealed);
    hideLabel?.classList.toggle('hidden', revealed);

    if (icon) {
      icon.style.maskImage = `var(--icon-eye${revealed ? 'Open' : 'Closed'})`;
    }
  });
}

export function setupPasswordChecklist(input: HTMLInputElement, rules: ValidationRule[] = passwordRules) {
  const list = input.closest<HTMLElement>('.form-group')?.querySelector<HTMLElement>('.password-checklist');
  if (!list) {
    return;
  }

  const update = () => {
    for (const rule of rules) {
      const passed = rule.test(input.value);
      const icon = list.querySelector<HTMLElement>(`[data-password-rule="${rule.key}"] .password-rule-icon`);
      if (!icon) {
        continue;
      }

      icon.textContent = passed ? '✓' : '✗';
      icon.classList.toggle('text-correct-green', passed);
      icon.classList.toggle('text-dark-sand', !passed);
    }
  };

  input.addEventListener('input', update);
  update();
}
